import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { TransakCard } from '@transak/ui-components-react';
import './Transak.css';


const API_BASE = 'https://api.e-pocketgateway.com/v1/api';
const PAYMENT_SOURCE = 'EPG16';


const CURRENCIES = ['AUD'];


const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;


// Fetches the user's public IP (swap with your shared util if you already have one)
export const getUserGeoInfo = async () => {
  try {
    const response = await fetch('https://ipapi.co/json/');
    const data = await response.json();
    return { ip: data.ip, country: data.country };
  } catch (error) {
    console.error('Error fetching geolocation:', error);
    return null;
  }
};


const TransakCardComponent = ({ environment = 'PRODUCTION' }) => {
  const cardRef = useRef(null);


  // Step 1 form state
  const [amount, setAmount] = useState('');
  const [currency, setCurrency] = useState('AUD');
  const [email, setEmail] = useState('');
  const [userIp, setUserIp] = useState('');
  const [ipLoading, setIpLoading] = useState(true);
  const [fieldErrors, setFieldErrors] = useState({});


  // Session returned by card-payment-link
  const [session, setSession] = useState(null); // { sessionId, pid, quoteId, amount, currency }


  // Shared UI state
  const [creatingSession, setCreatingSession] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isValid, setIsValid] = useState(false);
  const [requestId, setRequestId] = useState(null);


  // Fetch the user's IP once on mount
  useEffect(() => {
    let active = true;
    (async () => {
      const geo = await getUserGeoInfo();
      if (!active) return;
      if (geo?.ip) setUserIp(geo.ip);
      setIpLoading(false);
    })();
    return () => {
      active = false;
    };
  }, []);


  const validateForm = () => {
    const errors = {};
    const numericAmount = Number(amount);


    if (!amount || Number.isNaN(numericAmount) || numericAmount <= 0) {
      errors.amount = 'Enter an amount greater than 0.';
    }
    if (!currency) {
      errors.currency = 'Select a currency.';
    }
    if (!EMAIL_RE.test(email.trim())) {
      errors.email = 'Enter a valid email address.';
    }
    if (!userIp) {
      errors.ip = 'We couldn\u2019t detect your IP address. Check your connection and refresh.';
    }


    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };


  // Step 1: generate token  →  Step 2: create card payment link
  const handleCreateSession = async (e) => {
    e.preventDefault();
    setError(null);


    if (!validateForm()) return;


    setCreatingSession(true);
    try {
      // 1. Generate token
      const tokenRes = await axios.post(`${API_BASE}/generate-token`, {
        client_id: "c28ef6514f452845b3058818a0b4082f43fbc843",
        secret_key: "2f844415d62e11d4b97dea76170782c118f04087"
      });
      const token = tokenRes.data?.data?.token;
      if (!token) {
        throw new Error('Could not authenticate. Please try again.');
      }


      // 2. Create card payment link using the token
      const linkRes = await axios.post(
        `${API_BASE}/payments/card-payment-link`,
        {
          email: email.trim(),
          amount: Number(amount),
          currency,
          source: PAYMENT_SOURCE,
          user_ip: userIp
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );


      const data = linkRes.data?.data;
      if (!data?.sessionId || !data?.pid) {
        throw new Error('Payment session was not created. Please try again.');
      }


      setSession(data);
    } catch (err) {
      console.error(err);
      setError(
        err.response?.data?.message ||
          err.message ||
          'Something went wrong while starting your payment.'
      );
    } finally {
      setCreatingSession(false);
    }
  };


  const handleEditDetails = () => {
    setSession(null);
    setIsValid(false);
    setRequestId(null);
    setError(null);
  };


  const handleReady = () => {
    console.log('✅ Transak Card is ready');
  };


  const handleChange = ({ isValid: valid, errorCode }) => {
    setIsValid(valid);
    console.log('Form changed → Valid:', valid, 'Error:', errorCode);
  };


  // Step 3: card data captured → get redirect URL
  const handleSuccess = async ({ requestId: receivedRequestId }) => {
    console.log('🎉 Payment Data Captured! Request ID:', receivedRequestId);
    setRequestId(receivedRequestId);


    setLoading(true);
    setError(null);


    try {
      const response = await axios.post(`${API_BASE}/payments/redirect-url`, {
        pid: session.pid,
        requestId: receivedRequestId,
        sessionId: session.sessionId
      });


      const { redirectUrl: url } = response.data.data;


      if (url) {
        window.location.href = url;
      } else {
        setError('Redirect URL not received. Please try again.');
        setLoading(false);
      }
    } catch (err) {
      setError(
        err.response?.data?.message ||
          'Something went wrong while processing your payment.'
      );
      console.error(err);
      setLoading(false);
    }
  };


  const handleError = ({ code }) => {
    console.error('❌ Transak Error:', code);
    // setError('We couldn\u2019t validate your card details. Please check them and try again.');
  };


  const handleSubmit = () => {
    if (cardRef.current) {
      cardRef.current.submit();
      console.log('Submitting form...');
    } else {
      console.error('Card ref not available');
    }
  };


  return (
    <div className="tc-page">
      <div className="tc-card">
        <div className="tc-card-accent" />


        <div className="tc-header">
          <div className="tc-lock-badge" aria-hidden="true">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
              <rect x="5" y="11" width="14" height="9" rx="2" stroke="currentColor" strokeWidth="2" />
              <path d="M8 11V7a4 4 0 0 1 8 0v4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
            </svg>
          </div>
          <div>
            <h2 className="tc-title">{session ? 'Enter card details' : 'Payment details'}</h2>
            <p className="tc-subtitle">
              {session
                ? 'Your payment is encrypted and processed securely.'
                : 'Tell us how much you’d like to pay.'}
            </p>
          </div>
        </div>


        {/* ---------- STEP 1: details form ---------- */}
        {!session && (
          <form className="tc-form" onSubmit={handleCreateSession} noValidate>
            <div className="tc-row">
              <div className="tc-field tc-field-grow">
                <label className="tc-label" htmlFor="tc-amount">Amount</label>
                <input
                  id="tc-amount"
                  className={`tc-input ${fieldErrors.amount ? 'tc-input-error' : ''}`}
                  type="number"
                  inputMode="decimal"
                  min="0"
                  step="0.01"
                  placeholder="15.00"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  disabled={creatingSession}
                />
                {fieldErrors.amount && <span className="tc-field-error">{fieldErrors.amount}</span>}
              </div>


              <div className="tc-field tc-field-currency">
                <label className="tc-label" htmlFor="tc-currency">Currency</label>
                <select
                  id="tc-currency"
                  className={`tc-input ${fieldErrors.currency ? 'tc-input-error' : ''}`}
                  value={currency}
                  onChange={(e) => setCurrency(e.target.value)}
                  disabled={creatingSession}
                >
                  {CURRENCIES.map((c) => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
                {fieldErrors.currency && <span className="tc-field-error">{fieldErrors.currency}</span>}
              </div>
            </div>


            <div className="tc-field">
              <label className="tc-label" htmlFor="tc-email">Email</label>
              <input
                id="tc-email"
                className={`tc-input ${fieldErrors.email ? 'tc-input-error' : ''}`}
                type="email"
                autoComplete="email"
                placeholder="john@gmail.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={creatingSession}
              />
              {fieldErrors.email && <span className="tc-field-error">{fieldErrors.email}</span>}
            </div>


            <div className="tc-field">
              <label className="tc-label" htmlFor="tc-ip">IP address</label>
              <input
                id="tc-ip"
                className={`tc-input ${fieldErrors.ip ? 'tc-input-error' : ''}`}
                type="text"
                value={ipLoading ? 'Detecting…' : userIp}
                readOnly
                aria-readonly="true"
              />
              {fieldErrors.ip && <span className="tc-field-error">{fieldErrors.ip}</span>}
            </div>


            {error && (
              <div className="tc-banner tc-banner-error" role="alert">
                <span className="tc-banner-dot" />
                {error}
              </div>
            )}


            {creatingSession && (
              <div className="tc-banner tc-banner-info">
                <span className="tc-spinner" aria-hidden="true" />
                Setting up your secure payment…
              </div>
            )}


            <button
              type="submit"
              className="tc-submit"
              disabled={creatingSession || ipLoading}
            >
              {creatingSession ? 'Please wait…' : 'Continue to payment'}
            </button>
          </form>
        )}


        {/* ---------- STEP 2: card entry ---------- */}
        {session && (
          <>
            <div className="tc-summary">
              <div>
                <span className="tc-summary-label">Paying</span>
                <strong className="tc-summary-value">
                  {session.amount} {session.currency}
                </strong>
              </div>
              <button
                type="button"
                className="tc-link-btn"
                onClick={handleEditDetails}
                disabled={loading}
              >
                Edit details
              </button>
            </div>


            <div className="tc-field-wrap">
              <TransakCard
                ref={cardRef}
                sessionId={session.sessionId}
                environment={environment}
                onReady={handleReady}
                onChange={handleChange}
                onSuccess={handleSuccess}
                onError={handleError}
                className="transak-card"
              />
            </div>


            {error && (
              <div className="tc-banner tc-banner-error" role="alert">
                <span className="tc-banner-dot" />
                {error}
              </div>
            )}


            {loading && (
              <div className="tc-banner tc-banner-info">
                <span className="tc-spinner" aria-hidden="true" />
                Processing payment — redirecting you now…
              </div>
            )}


            <button
              onClick={handleSubmit}
              disabled={loading}
              className="tc-submit"
            >
              {loading ? 'Processing…' : 'Pay securely'}
            </button>
          </>
        )}
      </div>
    </div>
  );
};


export default TransakCardComponent;


